import { debounce, parseHTML } from './theme.js';

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-results]');
    this.empty = this.querySelector('[data-empty-state]');
    this.status = this.querySelector('[data-status]');
    this.reset = this.querySelector('[data-reset]');
    this.cache = new Map();
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', debounce(() => this.onInput(), 220));
    this.input.addEventListener('keydown', this.onKeydown);
    this.reset?.addEventListener('click', () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    });
    this.closest('dialog')?.addEventListener('dialog:open', () => {
      requestAnimationFrame(() => this.input.focus());
    });
  }

  get query() {
    return this.input.value.trim();
  }

  async onInput() {
    const q = this.query;
    if (this.reset) this.reset.hidden = !q;
    if (!q) {
      this.close();
      return;
    }
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    try {
      const html = this.cache.get(q) || (await this.fetch(q, this.controller.signal));
      this.cache.set(q, html);
      if (q !== this.query) return;
      this.show(html);
    } catch (e) {
      if (e.name !== 'AbortError') this.close();
    }
  }

  async fetch(q, signal) {
    const root = window.Diecela.routes.predictive_search_url;
    const params = new URLSearchParams({
      q,
      section_id: 'predictive-search',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[type]': 'product,collection,query,page,article',
    });
    const res = await fetch(`${root}?${params}`, { signal });
    if (!res.ok) throw new Error(res.status);
    const doc = parseHTML(await res.text());
    return doc.querySelector('[data-predictive-results]')?.innerHTML || '';
  }

  show(html) {
    this.results.innerHTML = html;
    this.results.hidden = false;
    if (this.empty) this.empty.hidden = true;
    this.input.setAttribute('aria-expanded', 'true');

    this.input.removeAttribute('aria-activedescendant');
    const count = this.results.querySelectorAll('[role="option"]').length;
    if (this.status) {
      const s = window.Diecela.strings;
      this.status.textContent = count ? s.searchResults.replace('[count]', count) : s.searchNoResults.replace('[terms]', this.query);
    }
    this.activeIndex = -1;
  }

  close() {
    this.results.hidden = true;
    this.results.innerHTML = '';
    if (this.empty) this.empty.hidden = false;

    this.input.setAttribute('aria-expanded', String(!!this.closest('header-search[data-open]')));
    this.input.removeAttribute('aria-activedescendant');
  }

  onKeydown = (e) => {
    const options = Array.from(this.results.querySelectorAll('[role="option"]'));
    if (!options.length || this.results.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + dir + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.activeIndex)));
      const active = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.activeIndex > -1) {
      e.preventDefault();
      options[this.activeIndex].querySelector('a')?.click();
    }
  };
}
customElements.define('predictive-search', PredictiveSearch);
